import type { SxProps, Theme } from "@mui/material/styles";

export type PtiMeasurementZone = "safe" | "caution" | "danger" | "unknown";

export const STEER_TREAD_MIN_32NDS = 4;
export const DRIVE_TRAILER_TREAD_MIN_32NDS = 2;

export function isSteerTirePosition(positionKey: string): boolean {
    return positionKey.startsWith("steer_");
}

export function getMinimumTreadDepth32nds(positionKey: string): number {
    return isSteerTirePosition(positionKey) ? STEER_TREAD_MIN_32NDS : DRIVE_TRAILER_TREAD_MIN_32NDS;
}

export function getTreadDepthZone(
    positionKey: string,
    tread32nds: number | null | undefined
): PtiMeasurementZone {
    if (tread32nds == null || Number.isNaN(tread32nds)) {
        return "unknown";
    }

    const minimum = getMinimumTreadDepth32nds(positionKey);
    if (tread32nds < minimum) {
        return "danger";
    }

    return "safe";
}

export function getBrakeMeasurementZone(mm: number | null | undefined): PtiMeasurementZone {
    if (mm == null || Number.isNaN(mm)) {
        return "unknown";
    }

    if (mm <= 3) {
        return "danger";
    }

    if (mm < 8) {
        return "caution";
    }

    return "safe";
}

export function getZoneLabel(zone: PtiMeasurementZone): string {
    switch (zone) {
        case "safe":
            return "Safe";
        case "caution":
            return "Caution";
        case "danger":
            return "Replace";
        default:
            return "";
    }
}

export function formatTreadDepth(value: number): string {
    return `${value}/32"`;
}

export function getTreadMinimumLabel(positionKey: string): string {
    const minimum = getMinimumTreadDepth32nds(positionKey);
    return `Min ${minimum}/32"`;
}

export function getBrakeScaleHint(): string {
    return "Safe ≥8mm · Caution 4–7mm · Replace ≤3mm";
}

/** Full scale explanation for tread depth info popover. */
export function getTreadScaleHint(positionKey: string): string {
    const minimum = getMinimumTreadDepth32nds(positionKey);
    const tireType = isSteerTirePosition(positionKey) ? "Steer" : "Drive / trailer";
    return `${tireType} tire minimum: ${minimum}/32". Measurements below ${minimum}/32" require replacement.`;
}

/** Short helper under tread input — current value zone only. */
export function getTreadZoneHelperText(
    positionKey: string,
    zone: PtiMeasurementZone
): string {
    const minimum = getMinimumTreadDepth32nds(positionKey);

    switch (zone) {
        case "safe":
            return `Safe ≥${minimum}/32"`;
        case "danger":
            return `<${minimum}/32" · Replace`;
        default:
            return "";
    }
}

/** Short helper under brake input — current value zone only. */
export function getBrakeZoneHelperText(zone: PtiMeasurementZone): string {
    switch (zone) {
        case "safe":
            return "Safe ≥8mm";
        case "caution":
            return "Caution 4–7mm";
        case "danger":
            return "≤3mm · Replace";
        default:
            return "";
    }
}

export function getChipColor(zone: PtiMeasurementZone): "default" | "success" | "warning" | "error" {
    switch (zone) {
        case "safe":
            return "success";
        case "caution":
            return "warning";
        case "danger":
            return "error";
        default:
            return "default";
    }
}

/** MUI TextField sx matching mobile zone border/background highlighting. */
export function getMeasurementTextFieldSx(
    zone: PtiMeasurementZone
): SxProps<Theme> | undefined {
    if (zone === "unknown") {
        return undefined;
    }

    const paletteKey =
        zone === "safe" ? "success" : zone === "caution" ? "warning" : "error";

    return {
        "& .MuiOutlinedInput-root": {
            bgcolor: (theme) => `${theme.palette[paletteKey].main}14`,
            "& fieldset": {
                borderColor: `${paletteKey}.main`,
                borderWidth: 2,
            },
            "&:hover fieldset": {
                borderColor: `${paletteKey}.main`,
            },
            "&.Mui-focused fieldset": {
                borderColor: `${paletteKey}.main`,
            },
        },
    };
}
